import {
  useEffect, useRef
} from 'react'
import type {
  ReactNode
} from 'react'
import {
  Bot
} from 'lucide-react'
import type {
  WhatsAppStatusResponse,
  SlackStatusResponse,
  SmsStatusResponse,
  IMessageStatusResponse,
} from '../services/api'
import {
  OnboardingEvents,
  trackEvent
} from '../analytics/posthog'
import {
  IMessageIcon,
  SlackIcon,
  SmsIcon,
  WhatsAppIcon,
} from './messaging-logos'

type MessagingChannel = 'whatsapp' | 'slack' | 'sms' | 'imessage'

export interface ResolvedChannel {
  channel: MessagingChannel
  label: string
  href: string
}

type ChannelLogo = (props: { className?: string }) => JSX.Element

const CHANNEL_LOGOS: Record<MessagingChannel, { Logo: ChannelLogo, logoClassName: string }> = {
  whatsapp: { Logo: WhatsAppIcon, logoClassName: '' },
  slack: { Logo: SlackIcon, logoClassName: '' },
  sms: { Logo: SmsIcon, logoClassName: 'text-gray-600' },
  imessage: { Logo: IMessageIcon, logoClassName: '' },
}

interface ChannelDeepLinkProps extends ResolvedChannel {
  ariaLabel: string
  className: string
  logoSize: string
  onClick: () => void
  // Rendered after the logo — the label, where a surface shows one.
  children?: ReactNode
}

/**
 * One anchor straight into the conversation on a channel. Shared by every
 * surface that offers "open the chat" links, so they cannot disagree on the
 * mark, the target, or the rel.
 */
export function ChannelDeepLink({
  channel, label, href, ariaLabel, className, logoSize, onClick, children,
}: ChannelDeepLinkProps): JSX.Element {
  const { Logo, logoClassName } = CHANNEL_LOGOS[channel]
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      title={`Open ${label}`}
      aria-label={ariaLabel}
      className={className}
    >
      <Logo className={`${logoSize} ${logoClassName}`} />
      {children}
    </a>
  )
}

interface AssistantWaitingCTAProps {
  assistantName: string
  assistantPictureUrl?: string | null | undefined
  whatsapp: WhatsAppStatusResponse | null
  slack: SlackStatusResponse | null
  sms: SmsStatusResponse | null
  imessage: IMessageStatusResponse | null
}

/**
 * Resolve every messaging channel the assistant is reachable on, each with a
 * deep link straight into that conversation. Ordered by the backend's
 * owner-preferred-channel priority (WhatsApp → Slack → SMS → iMessage). Empty
 * when no channel is connected, since there is nothing to link into.
 */
export function resolveConnectedChannels(
  whatsapp: WhatsAppStatusResponse | null,
  slack: SlackStatusResponse | null,
  sms: SmsStatusResponse | null,
  imessage: IMessageStatusResponse | null,
): ResolvedChannel[] {
  const channels: ResolvedChannel[] = []
  if (whatsapp?.connected && whatsapp.twilio_whatsapp_number) {
    const digits = whatsapp.twilio_whatsapp_number.replace(/\D/g, '')
    if (digits) {
      channels.push({ channel: 'whatsapp', label: 'WhatsApp', href: `https://wa.me/${digits}` })
    }
  }
  if (slack?.connected && slack.deep_link_url) {
    channels.push({ channel: 'slack', label: 'Slack', href: slack.deep_link_url })
  }
  if (sms?.connected && sms.twilio_sms_number) {
    channels.push({ channel: 'sms', label: 'SMS', href: `sms:${sms.twilio_sms_number}` })
  }
  if (imessage?.connected && imessage.linq_from_number) {
    channels.push({ channel: 'imessage', label: 'iMessage', href: `sms:${imessage.linq_from_number}` })
  }
  return channels
}

/**
 * Nudge at the bottom of the Integrations page: shows the assistant's face and,
 * next to it, the messaging apps it's reachable on as clickable logos that
 * deep-link straight into the conversation.
 */
export function AssistantWaitingCTA({
  assistantName,
  assistantPictureUrl,
  whatsapp,
  slack,
  sms,
  imessage,
}: AssistantWaitingCTAProps): JSX.Element | null {
  const channels = resolveConnectedChannels(whatsapp, slack, sms, imessage)
  const channelsKey = channels.map((c) => c.channel).join(',')
  const cardRef = useRef<HTMLDivElement | null>(null)
  const viewedTracked = useRef(false)

  // Count the impression only when the card scrolls into view. It sits at the
  // bottom of the Integrations page, so firing on render would log below-the-fold
  // mounts and inflate the viewed→clicked rate.
  useEffect(() => {
    if (!channelsKey || viewedTracked.current) return
    const el = cardRef.current
    if (!el) return

    const fire = (): void => {
      if (viewedTracked.current) return
      viewedTracked.current = true
      trackEvent(OnboardingEvents.ASSISTANT_MESSAGE_NUDGE_VIEWED, { channels: channelsKey })
    }

    if (typeof IntersectionObserver === 'undefined') {
      fire()
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          fire()
          observer.disconnect()
        }
      },
      { threshold: 0.5 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [channelsKey])

  if (channels.length === 0) {
    return null
  }

  const handleClick = (channel: MessagingChannel): void => {
    trackEvent(OnboardingEvents.ASSISTANT_MESSAGE_NUDGE_CLICKED, { channel })
  }

  return (
    <div ref={cardRef} className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-soft sm:p-6">
      <h3 className="text-base font-semibold text-gray-900 sm:text-lg">
        You already have a message waiting from {assistantName}
      </h3>
      <p className="mt-0.5 text-sm text-gray-500">
        Tap a logo to pick up the conversation right where you left off.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {assistantPictureUrl ? (
          <img
            src={assistantPictureUrl}
            alt={assistantName}
            className="h-12 w-12 shrink-0 rounded-full object-cover ring-2 ring-gray-100"
          />
        ) : (
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-600 ring-2 ring-gray-100">
            <Bot className="h-6 w-6 text-white" />
          </div>
        )}

        {channels.map((resolved) => (
          <ChannelDeepLink
            key={resolved.channel}
            {...resolved}
            ariaLabel={`Open the conversation in ${resolved.label}`}
            onClick={() => handleClick(resolved.channel)}
            logoSize="h-5 w-5"
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700"
          >
            {resolved.label}
          </ChannelDeepLink>
        ))}
      </div>
    </div>
  )
}
